
import React, { useState, useEffect } from 'react';
import { ServicePackage, Language, SocialPlatform } from '../types';
import { TRANSLATIONS, getPlatformLabel } from '../translations';
import { X, Save, AlertCircle } from 'lucide-react';

interface ServiceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (pkg: ServicePackage) => void;
  service: ServicePackage | null; // null for new service
  language: Language;
}

export const ServiceEditModal: React.FC<ServiceEditModalProps> = ({ isOpen, onClose, onSave, service, language }) => {
  const t = TRANSLATIONS[language];
  
  const [formData, setFormData] = useState<Partial<ServicePackage>>({
    title: '',
    platform: SocialPlatform.TWITTER,
    price: 0,
    deliveryDays: 3,
    description: '',
    features: []
  });

  const [featuresText, setFeaturesText] = useState('');

  useEffect(() => {
    if (service) {
      setFormData(service);
      setFeaturesText(service.features.join('\n'));
    } else {
      // Reset for new
      setFormData({
        title: '',
        platform: SocialPlatform.TWITTER,
        price: 0,
        deliveryDays: 3,
        description: '',
        features: []
      });
      setFeaturesText('');
    }
  }, [service, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const featuresArray = featuresText.split('\n').filter(line => line.trim() !== '');
    
    const newService: ServicePackage = {
      id: service ? service.id : `new_${Date.now()}`,
      title: formData.title || 'Untitled Service',
      description: formData.description || '',
      platform: formData.platform || SocialPlatform.TWITTER,
      price: Number(formData.price) || 0,
      deliveryDays: Number(formData.deliveryDays) || 1,
      features: featuresArray
    };

    onSave(newService);
  };

  const calculateNetEarnings = () => {
      const price = Number(formData.price) || 0;
      return (price * 0.80).toFixed(2);
  };

  const calculateFee = () => {
      const price = Number(formData.price) || 0;
      return (price * 0.20).toFixed(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">
            {service ? t.editService : t.addService}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t.serviceTitle}</label>
            <input 
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">{t.selectPlatform}</label>
              <select 
                value={formData.platform}
                onChange={(e) => setFormData({...formData, platform: e.target.value as SocialPlatform})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              >
                {Object.values(SocialPlatform).map(p => (
                    <option key={p} value={p}>{getPlatformLabel(p, language)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">{t.priceUsdt}</label>
              <input 
                type="number"
                required
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: Number(e.target.value)})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Fee Calculation Display */}
          <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700 flex items-center gap-3">
              <AlertCircle size={18} className="text-indigo-400" />
              <div className="flex-1 text-xs">
                  <div className="flex justify-between text-slate-400 mb-1">
                      <span>{t.platformFee} (20%)</span>
                      <span>-${calculateFee()}</span>
                  </div>
                  <div className="flex justify-between font-bold text-emerald-400">
                      <span>{t.netToKol}</span>
                      <span>${calculateNetEarnings()}</span>
                  </div>
              </div>
          </div>

          <div>
             <label className="block text-sm font-medium text-slate-300 mb-1">{t.deliveryTime}</label>
             <input 
                type="number"
                required
                min="1"
                value={formData.deliveryDays}
                onChange={(e) => setFormData({...formData, deliveryDays: Number(e.target.value)})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t.description}</label>
            <textarea 
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t.featuresList}</label>
            <textarea 
              rows={4}
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
              placeholder="- High Quality&#10;- 24h Pinned"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none font-mono text-sm"
            />
          </div>

          <div className="pt-4 flex gap-3">
             <button 
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
             >
                {t.cancel}
             </button>
             <button 
                type="submit"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
             >
                <Save size={18} /> {t.saveService}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};
